// ─────────────────────────────────────────────────────────────────────────────
// VEC — CENTRAL CONTENT FILE
// Edit company content, projects, capabilities, images and contact details here.
// To replace any stock image with real VEC photography, swap the `image` URL.
// ─────────────────────────────────────────────────────────────────────────────

const u = (id, w = 1400) =>
    `https://images.unsplash.com/${id}?w=${w}&q=72&auto=format&fit=crop`;

export const IMAGES = {
    hero: u("photo-1473341304170-971dccb5ac1e", 2000), // transmission towers at sunset
    about: "/about-site-operations.webp", // real VEC site photography — engineers at substation
    substationLarge: u("photo-1509390144018-eeaf65052242", 1600), // substation transformer
    transmissionLarge: u("photo-1644567103258-6da3857faab4", 1600), // transmission towers
    solarAerial: u("photo-1497435334941-8c899ee9e8e9", 1400), // solar farm aerial
    windDusk: u("photo-1467533003447-e295ff1b0435", 1400), // wind turbines at dusk
    safety: "/safety-ppe.webp", // PPE and safety gear at a VEC substation at sunset
    peopleA: u("photo-1613665813446-82a78c468a1d", 1200), // rooftop solar installation
    peopleB: u("photo-1592833159155-c62df1b65634", 1200), // solar array hillside
};

export const LOGO_SRC = "/vec-logo-transparent.png";

export const NAV_LINKS = [
    { label: "About", href: "#about", testId: "nav-about-link" },
    { label: "Capabilities", href: "#capabilities", testId: "nav-capabilities-link" },
    { label: "Projects", href: "#projects", testId: "nav-projects-link" },
    { label: "Safety & Quality", href: "#safety", testId: "nav-safety-link" },
    { label: "Careers", href: "/careers", testId: "nav-careers-link" },
    { label: "Contact", href: "#contact", testId: "nav-contact-link" },
];

// Verified company figures only — do not add unverified metrics.
export const STATS = [
    { value: 1997, over: "Est.", label: "Years of Experience", plain: true },
    { value: 400, over: "Up to", suffix: " kV", label: "Substation Experience" },
    { value: 230, over: "Up to", suffix: " kV", label: "Transmission Line Experience" },
    { value: 12, over: "Current", prefix: "₹", suffix: "+ Cr", label: "Turnover" },
];

export const CAPABILITIES = [
    {
        num: "01",
        title: "Transmission",
        tag: "TRANSMISSION",
        image: u("photo-1473341304170-971dccb5ac1e", 1200),
        alt: "Transmission lines and pylons carrying power across open terrain",
        items: [
            "Transmission line erection",
            "Stringing and conductor work",
            "OPGW-related works",
            "Transmission line maintenance",
        ],
    },
    {
        num: "02",
        title: "Substations",
        tag: "SUBSTATION",
        image: "/capabilities/substation-om.webp",
        alt: "VEC substation under operation and maintenance — gantry structures, busbars and a wind turbine beyond",
        items: [
            "Substation O&M",
            "Electrical maintenance",
            "Yard works",
            "Equipment installation and associated works",
        ],
    },
    {
        num: "03",
        title: "Renewable Energy",
        tag: "RENEWABLE",
        image: u("photo-1466611653911-95081537e5b7", 1200),
        alt: "Wind turbines operating at sunset",
        items: [
            "Wind farm O&M",
            "Electrical infrastructure",
            "Balance-of-plant related works",
        ],
    },
    {
        num: "04",
        title: "Civil & Infrastructure",
        tag: "CIVIL WORKS",
        image: "/capabilities/civil-works.webp",
        alt: "Transformer installed on a freshly completed concrete foundation with yard fencing — VEC civil and infrastructure works",
        items: [
            "Substation civil works",
            "Foundations",
            "Yard development",
            "Associated infrastructure works",
        ],
    },
];

// Exact project data as supplied by VEC — do not alter facts.
export const PROJECTS = [
    {
        id: 1,
        client: "Renew Power",
        category: "Substation O&M",
        voltage: "400/33 kV",
        location: "Tuljapur, Osmanabad, Maharashtra",
        scope: "O&M of 400/33KV Substation, Tuljapur, Osmanabad, Maharashtra",
        meta: ["Operation & Maintenance", "400/33 kV Substation"],
        image: "/projects/prj-01.webp",
    },
    {
        id: 2,
        client: "Renew Power",
        category: "Substation O&M",
        voltage: "220/33 kV",
        location: "Gadag, Karnataka",
        scope: "O&M of 220/33KV Substation, Gadag, Karnataka",
        meta: ["Operation & Maintenance", "220/33 kV Substation"],
        image: "/projects/prj-02.webp",
    },
    {
        id: 3,
        client: "Suzlon",
        category: "Substation O&M",
        voltage: "220/33 kV",
        location: "Mendhgiri, Sangli, Maharashtra",
        scope: "O&M of 220/33KV Substation, Mendhgiri, Sangli, Maharashtra",
        meta: ["Operation & Maintenance", "220/33 kV Substation"],
        image: "/projects/prj-03.webp",
    },
    {
        id: 4,
        client: "JSW Energy",
        category: "Substation O&M",
        voltage: "220/33 kV",
        location: "Barul, Tuljapur, Maharashtra",
        scope: "O&M of 220/33KV Substation, Barul, Tuljapur, Maharashtra",
        meta: ["Operation & Maintenance", "220/33 kV Substation"],
        image: "/projects/prj-04.webp",
    },
    {
        id: 5,
        client: "Renew Power",
        category: "Substation O&M",
        voltage: "220/33 kV",
        location: "Waiphale, Sangli, Maharashtra",
        scope: "O&M of 220/33KV Substation, Waiphale, Sangli, Maharashtra",
        meta: ["Operation & Maintenance", "220/33 kV Substation"],
        image: "/projects/prj-05.webp",
    },
    {
        id: 6,
        client: "JSW Energy",
        category: "Substation O&M",
        voltage: "110/33 kV",
        location: "Jath, Sangli, Maharashtra",
        scope: "O&M of 110/33KV Substation, Jath, Sangli, Maharashtra",
        meta: ["Operation & Maintenance", "110/33 kV Substation"],
        image: "/projects/prj-06.webp",
    },
    {
        id: 7,
        client: "Greenko Group, Hero Futures, Inox Power",
        category: "Substation O&M",
        voltage: "220/33 kV",
        location: "Khanapur, Sangli, Maharashtra",
        scope: "O&M of 220/33KV Substation, Khanapur, Sangli, Maharashtra",
        meta: ["Operation & Maintenance", "220/33 kV Substation"],
        image: "/projects/prj-07.webp",
    },
    {
        id: 8,
        client: "Regen Powertech",
        category: "Substation O&M",
        voltage: "132/33 kV",
        location: "Hiwarwadi, Satara, Maharashtra",
        scope: "O&M of 132/33KV Substation, Hiwarwadi, Satara, Maharashtra since commissioning",
        meta: ["Operation & Maintenance", "132/33 kV Substation", "O&M Since Commissioning"],
        image: "/projects/prj-08.webp",
    },
];

export const PRESENCE_NOTE = "Areas of project presence — not office locations";

export const SAFETY_PILLARS = [
    {
        num: "01",
        title: "Safety First",
        description: "Strong safety practices across project sites and operations.",
    },
    {
        num: "02",
        title: "Quality Driven",
        description: "Execution aligned with project specifications, standards and client requirements.",
    },
    {
        num: "03",
        title: "Responsible Execution",
        description: "Reliable project delivery with focus on people, assets and the environment.",
    },
];

export const WHY_VEC = [
    {
        num: "01",
        title: "Power Infrastructure Focus",
        description: "Focused capabilities across transmission and electrical infrastructure.",
    },
    {
        num: "02",
        title: "Field Execution",
        description: "Strong emphasis on practical, on-ground project execution.",
    },
    {
        num: "03",
        title: "Integrated Capabilities",
        description: "Electrical, civil, structural and renewable infrastructure capabilities.",
    },
    {
        num: "04",
        title: "Regional Presence",
        description: "Presence across key locations in Maharashtra and neighbouring states.",
    },
];

export const CLIENTS = [
    { name: "Mahavitaran (MSEDCL)", logo: "/clients/mahavitaran.png" },
    { name: "Mahatransco (MSETCL)", logo: "/clients/mahatransco.png" },
    { name: "Torrent Power", logo: "/clients/torrent.png" },
    { name: "Renew Power", logo: "/clients/renew.png" },
    { name: "Suzlon", logo: "/clients/suzlon.png" },
    { name: "JSW Energy", logo: "/clients/jsw.png" },
    { name: "Greenko", logo: "/clients/greenko.png" },
    { name: "Hero Future Energies", logo: "/clients/hero.png" },
    { name: "ReGen Powertech", logo: "/clients/regen.png" },
    { name: "Inox Green Energy Services", logo: "/clients/inox.png" },
    { name: "Continuum Green Energy", logo: "/clients/continuum.png" },
    { name: "Vena Group", logo: "/clients/vena.png" },
    { name: "Enel Green Power", logo: "/clients/enel.png" },
    { name: "Reliance Power", logo: "/clients/reliance.png" },
    { name: "Apraava Energy", logo: "/clients/apraava.png" },
];

export const CONTACT_INFO = {
    phone: "+91 9604054488",
    email: "info@vijayanandengineers.com, vijayanand.eng@gmail.com",
    address: "A/P Limb, Tal Dist Satara, Maharashtra",
};

export const MARQUEE_ITEMS = [
    "Substation O&M",
    "Pole Erection",
    "Transmission Lines",
    "Solar Infrastructure",
    "Wind Infrastructure",
    "Transmitting a Brighter Tomorrow",
];
